// 1:1 port from age-of-mcp v3 (harbour.ts). The flattened part literals are
// reconstructed from the original helper functions + loops; geometry is
// parity-gated identical.
//
// v3 harbour — A DOCK READS BY ITS DECK. Authored for the 45° top-down
// camera: the whole 2x2 plot is a plank deck of bold alternating board runs
// (light/dark wood) standing on pilings over the water, with mooring bollards
// along one edge, a small harbourmaster's hut under a banner-red gable, a slim
// timber loading crane swinging its jib out over the water and a big coiled
// rope on the boards.

import { defineModel, styleV3, type Part } from "../../lib";

const { box, cyl, gable, poles, slab, CANVAS, ROOF_TRADE, PLASTER, TIMBER, WOOD, WOOD_DK } = styleV3;

const DECK_TOP = 0.42;       // plank surface height
const PILE     = "#4e3a20";  // pilings / bollards — dark waterlogged timber

// The plank deck: five board runs spanning the plot, alternating shades.
function deck(): Part[] {
  const out: Part[] = [];
  const runs = [-0.74, -0.37, 0, 0.37, 0.74];
  runs.forEach((z, i) => {
    out.push(box(1.84, 0.08, 0.35, i % 2 === 0 ? WOOD : WOOD_DK, [0, DECK_TOP - 0.04, z]));
  });
  return out;
}

// The pilings carrying the deck — proud corner posts driven into the water.
function pilings(): Part[] {
  const out: Part[] = [];
  for (const x of [-0.74, 0.74]) {
    for (const z of [-0.74, 0.74]) {
      out.push(cyl(0.08, 0.09, DECK_TOP + 0.12, 5, PILE, [x, (DECK_TOP + 0.12) / 2, z]));
    }
  }
  return out;
}

// Mooring bollards along the seaward (+z) edge.
function bollards(): Part[] {
  return [-0.55, 0.05, 0.62].map(x =>
    cyl(0.06, 0.08, 0.22, 6, PILE, [x, DECK_TOP + 0.11, 0.78]));
}

// The harbourmaster's hut on the back corner: limewashed walls under a
// banner-red gable.
function hut(): Part[] {
  return [
    box(0.62, 0.46, 0.58, PLASTER, [-0.5, DECK_TOP + 0.23, -0.5]),
    gable(0.72, 0.7, 0.26, ROOF_TRADE, -0.5, DECK_TOP + 0.46, -0.5, "x"),
  ];
}

// A slim timber loading crane: mast, jib swung out over the water, and the
// hanging rope with its hook block.
function crane(): Part[] {
  const mx = 0.55, mz = -0.35, mh = 0.8;
  return [
    cyl(0.045, 0.06, mh, 5, TIMBER, [mx, DECK_TOP + mh / 2, mz]),
    { ...box(0.06, 0.06, 0.74, TIMBER, [mx, DECK_TOP + mh - 0.04, mz + 0.32]),
      rotation: [0.18, 0, 0] },
    box(0.018, 0.3, 0.018, "#3a3128", [mx, DECK_TOP + mh - 0.2, mz + 0.66]),
    box(0.07, 0.06, 0.07, WOOD_DK, [mx, DECK_TOP + mh - 0.38, mz + 0.66]),
  ];
}

// A big coiled rope lying on the boards — dockside set dressing from above.
function ropeCoil(): Part {
  return cyl(0.16, 0.16, 0.05, 8, CANVAS, [0.18, DECK_TOP + 0.025, 0.3]);
}

function partsFor(s: number): Part[] {
  if (s <= 0) return [slab(1.84, 1.84)];
  if (s === 1) return [slab(1.84, 1.84), ...pilings()];
  if (s === 2) return [slab(1.84, 1.84), ...pilings(), ...deck(), ...poles(1.7, 1.7, 0.85)];
  return [
    slab(1.84, 1.84),
    ...pilings(),
    ...deck(),
    ...bollards(),
    ...hut(),
    ...crane(),
    ropeCoil(),
  ];
}

export const harbourV3 = defineModel({
  id: "harbour-v3",
  tiles: { w: 2, h: 2 },
  batchId: "45-optimized",
  name: "Harbour (v3)",
  tags: ["building","harbour","low-poly","age-of-mcp","v3","water"],
  summary: "Low-poly harbour.",
  description: "A timber dock reaching out over the water from the shore.",
  author: "age-of-mcp",
  footprint: 1,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => partsFor(0) },
    { id: "stage-1", label: "Lower", parts: () => partsFor(1) },
    { id: "stage-2", label: "Upper", parts: () => partsFor(2) },
    { id: "complete", label: "Complete", parts: () => partsFor(3) },
  ],
  animations: [],
});
